import React, { useEffect, useMemo } from 'react'
import { graphql } from 'gatsby'
import Seo from '../components/Seo'
import Layout from '../components/Layout'
import Intro from '../components/Intro'
import Main from '../components/Main'
import Button from '../components/Button'
import BlogTile from '../components/BlogTile'
import Gallery from '../components/Gallery'
import { useLangContext } from '../context/lang.context'
import { useFormContext } from '../context/form.context'
import { globals, s, sHtml } from '../style'

// Function to parse HTML and extract gallery divs
const parseArticleWithGalleries = (html) => {
  if (!html) return []
  
  const parts = []
  // Match div with class containing "post-gallery" (handles class="post-gallery" or class="other post-gallery" etc.)
  const galleryRegex = /<div\s+class=(["'])[^"']*\bpost-gallery\b[^"']*\1>(.*?)<\/div>/gs
  
  let match
  let lastIndex = 0
  
  while ((match = galleryRegex.exec(html)) !== null) {
    // Add HTML before the gallery
    if (match.index > lastIndex) {
      parts.push({
        type: 'html',
        content: html.substring(lastIndex, match.index)
      })
    }
    
    // Extract images from the gallery div
    // match[1] is the quote character, match[2] is the content
    const galleryContent = match[2]
    const imgRegex = /<img\s+([^>]*?)>/g
    const images = []
    let imgMatch
    
    while ((imgMatch = imgRegex.exec(galleryContent)) !== null) {
      const imgAttrs = imgMatch[1]
      const srcMatch = imgAttrs.match(/src=["']([^"']+)["']/)
      const altMatch = imgAttrs.match(/alt=["']([^"']*)["']/)
      
      if (srcMatch) {
        images.push({
          url: srcMatch[1],
          width: 1280,
          height: 720,
        })
      }
    }
    
    // Add gallery if images were found
    if (images.length > 0) {
      parts.push({
        type: 'gallery',
        images: images
      })
    }
    
    lastIndex = match.index + match[0].length
  }
  
  // Add remaining HTML
  if (lastIndex < html.length) {
    parts.push({
      type: 'html',
      content: html.substring(lastIndex)
    })
  }
  
  // If no galleries were found, return the original HTML as a single part
  if (parts.length === 0) {
    parts.push({
      type: 'html',
      content: html
    })
  }
  
  return parts
}

export default function BlogTemplate({ data, pageContext }) {
  const { lang } = useLangContext()
  const { setFormType } = useFormContext()

  // const blog = data.blogs.find((node) => node.lang.slug === lang) || data.blogs.find((node) => node.lang.slug === 'pl')
  const blog = data.blogs

  const {
    blog_slug,
    title,
    description,
    h1,
    seo_title,
    seo_desc,
    image,
    body,
    gallery,
    keywords,
    related,
    createdAt,
    updatedAt,
  } = blog

  // Parse article HTML to extract galleries
  const articleParts = useMemo(() => {
    return parseArticleWithGalleries(body?.childMarkdownRemark?.html)
  }, [body?.childMarkdownRemark?.html])

  const others = data.others.nodes
    .filter((node) => node.lang.slug === 'pl')
    .map(({ title, description, image, blog_slug }) => ({
      title,
      desc: description,
      slug: blog_slug,
      image,
    }))

  const button = {
    text: {
      pl: 'Czytaj artykuł',
      en: 'Read the article',
    },
    action: 'SCROLL',
  }

  const buttonBack = {
    text: {
      pl: 'Powrót do wszystkich artykułów',
      en: 'Back to all articles ',
    },
    link: '/blog/',
  }

  const relatedContent = {
    title: {
      pl: 'Powiązane Artykuły',
      en: 'Related Articles',
    },
    buttonMore: {
      text: {
        pl: 'Więcej artykułów',
        en: 'More articles',
      },
      link: '/blog/',
    },
  }

  const relatedArticles = related?.map(
    ({ title, description, image, blog_slug }) => ({
      title,
      desc: description,
      slug: blog_slug,
      image,
    })
  )

  useEffect(() => {
    setFormType('MESSAGE')
  }, [])

  return (
    <Layout>
      <Seo
        title={seo_title}
        desc={seo_desc}
        imageUrl={image.file.url}
        keywords={keywords}
        url={'blog/' + blog_slug}
      >
        <script type='application/ld+json' data-react-helmet='true'>{`{
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "${blog_slug}",
      "url": "https://seaandair.pl/blog/${blog_slug}/"
    },
    "headline": "${title}",
    "image": "${image.file.url}",
    "datePublished": "${createdAt}",
    "dateModified": "${updatedAt}",
    "author": {
      "@type": "Person",
      "name": "Omida Sea And Air",
      "url": "https://seaandair.pl/blog/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Omida Sea And Air S.A.",
      "logo": {
        "@type": "ImageObject",
        "url": "https://seaandair.pl/icons/icon-512x512.png"
      }
    }
  }`}</script>
      </Seo>
      <Intro
        title={title}
        desc={description}
        button={button}
        image={image}
        position='center'
      />
      <Main
        h={1}
        title={h1}
        subtitle={'Sea & Air Blog'}
        // blog={body.body}
        toc={body.childMarkdownRemark.headings}
        // article={body.childMarkdownRemark.html}
        maxWidth={globals.blogMaxWidth}
        extraCssTitle={{
          [s.lg]: {
            maxWidth: '100%',
          },
        }}
      >
        {articleParts.map((part, index) => {
          if (part.type === 'gallery') {
            return <Gallery key={`gallery-${index}`} images={part.images} inner />
          } else if (part.content && part.content.trim()) {
            return (
              <article
                key={`html-${index}`}
                dangerouslySetInnerHTML={{ __html: part.content }}
                css={[sHtml, sHtmlWrapper]}
              />
            )
          }
          return null
        })}
        {gallery && <Gallery images={gallery} inner />}
        {others && (
          <div css={sRelatedWrapper}>
            <h2>Przeczytaj również</h2>
            <div css={sBlogs}>
              {others.map((other, id) => (
                <BlogTile key={id} data={other} />
              ))}
            </div>
          </div>
        )}
        <Button link={buttonBack.link}>{buttonBack.text[lang]}</Button>
      </Main>
      {relatedArticles && (
        <Main s={1} title={relatedContent.title[lang]}>
          <div css={sBlogs}>
            {relatedArticles.map((blog, id) => (
              <BlogTile key={id} data={blog} />
            ))}
          </div>
          <Button link={relatedContent.buttonMore.link}>
            {relatedContent.buttonMore.text[lang]}
          </Button>
        </Main>
      )}
    </Layout>
  )
}

const sHtmlWrapper = {
  '.table-of-contents': {
    marginBottom: '3rem',
  },
  '.table-of-contents li': {
    marginBottom: 0,
    fontSize: '1.25rem',
    lineHeight: '2em',
  },
  '.table-of-contents li a': {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  'li': {
    marginBottom: 0,
    fontSize: '1.25rem',
    lineHeight: '2em',
  },
  'li a': {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  'ul, p': {
    marginBottom: '2rem',
  }
}

const sRelatedWrapper = {
  marginTop: '6rem',
}

const sBlogs = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '4rem',
  margin: '4rem 0',
  [s.sm_down]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    gap: '2rem',
    margin: '4rem 0 2rem',
  },
}

export const query = graphql`
  query ($slug: String!, $category: String) {
    blogs: contentfulBlog(blog_slug: { eq: $slug }) {
      lang {
        slug
      }
      createdAt
      updatedAt
      blog_slug
      title
      description
      seo_title
      seo_desc
      keywords
      h1
      body {
        childMarkdownRemark {
          html
          headings(depth: h2) {
            id
            value
          }
        }
      }
      image {
        gatsbyImageData(
          layout: FIXED
          quality: 100
          placeholder: BLURRED
          formats: [WEBP, AVIF]
        )
        file {
          url
        }
      }
      gallery {
        gatsbyImageData(placeholder: DOMINANT_COLOR, formats: [WEBP, AVIF])
        width
        url
        height
      }
      # related {
      #   blog_slug
      #   title
      #   description
      #   image {
      #     gatsbyImageData(
      #       layout: FIXED
      #       quality: 100
      #       placeholder: BLURRED
      #       formats: [JPG]
      #     )
      #   }
      # }
    }
    others: allContentfulBlog(
      limit: 4
      sort: { fields: date, order: DESC }
      filter: {
        blog_categories: { elemMatch: { blog_category: { eq: $category } } }
        blog_slug: { ne: $slug }
      }
    ) {
      nodes {
        blog_slug
        lang {
          slug
        }
        title
        description
        image {
          gatsbyImageData(
            layout: CONSTRAINED
            quality: 100
            placeholder: DOMINANT_COLOR
            formats: [JPG]
            aspectRatio: 1.77
          )
        }
      }
    }
  }
`
